import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PHASES } from "@/lib/reproData";

const C = 150;
const R = 118;
const pt = (frac, r) => {
  const a = (frac * 360 - 90) * (Math.PI / 180);
  return [C + r * Math.cos(a), C + r * Math.sin(a)];
};
const arc = (f1, f2, r) => {
  const [x1, y1] = pt(f1, r);
  const [x2, y2] = pt(f2, r);
  return `M ${x1} ${y1} A ${r} ${r} 0 ${f2 - f1 > 0.5 ? 1 : 0} 1 ${x2} ${y2}`;
};

export default function Wheel({ tone }) {
  const [len, setLen] = useState(28);
  const [day, setDay] = useState(3);
  const scale = len / 28;

  const phases = useMemo(
    () => PHASES.map((p) => ({ ...p, a: Math.round((p.from - 1) * scale) + 1, b: Math.round(p.to * scale) })),
    [scale]
  );
  const current = phases.find((p) => day >= p.a && day <= p.b) || phases[phases.length - 1];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="repro-wheel">
      <div className="lg:col-span-6 flex justify-center">
        <svg viewBox="0 0 300 300" className="w-full max-w-[380px]">
          {phases.map((p) => (
            <path key={p.k} d={arc((p.a - 1) / len, p.b / len, R)} fill="none" stroke={p.tone} strokeWidth={current.k === p.k ? 26 : 15}
              strokeLinecap="butt" opacity={current.k === p.k ? 0.95 : 0.34} onClick={() => setDay(p.a)} style={{ cursor: "pointer", transition: "stroke-width 0.3s, opacity 0.3s" }} />
          ))}
          {Array.from({ length: len }, (_, i) => {
            const [x, y] = pt((i + 0.5) / len, R + 22);
            const on = day === i + 1;
            return (
              <g key={i} onClick={() => setDay(i + 1)} style={{ cursor: "pointer" }}>
                <circle cx={x} cy={y} r={on ? 9 : 6.5} fill={on ? tone : "rgba(58,26,42,0.1)"} />
                {(i + 1) % 7 === 0 || on ? <text x={x} y={y + 3.4} textAnchor="middle" fontSize="8" fill={on ? "#fff" : "rgba(58,26,42,0.55)"} fontFamily="Outfit, sans-serif">{i + 1}</text> : null}
              </g>
            );
          })}
          <text x={C} y={C - 12} textAnchor="middle" fontSize="11" fill="rgba(58,26,42,0.45)" fontFamily="Outfit, sans-serif" letterSpacing="2">DAY</text>
          <text x={C} y={C + 26} textAnchor="middle" fontSize="42" fill="#3A1A2A" fontFamily="Outfit, sans-serif">{day}</text>
          <text x={C} y={C + 48} textAnchor="middle" fontSize="11" fill={current.tone} fontFamily="Outfit, sans-serif">{current.name.toLowerCase()}</text>
        </svg>
      </div>

      <div className="lg:col-span-6">
        <div className="flex items-center gap-4 flex-wrap">
          <p className="text-[12.5px]" style={{ color: "rgba(58,26,42,0.55)" }}>My cycle is usually</p>
          <input type="range" min={24} max={35} value={len} onChange={(e) => { const v = Number(e.target.value); setLen(v); setDay(Math.min(day, v)); }}
            className="flex-1 min-w-[140px]" style={{ accentColor: tone }} aria-label="cycle length" data-testid="wheel-length" />
          <p className="font-display text-2xl tabular-nums" style={{ color: tone }}>{len}<span className="text-[13px]" style={{ color: "rgba(58,26,42,0.4)" }}> days</span></p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={current.k} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="mt-7">
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: current.tone }}>days {current.a}–{current.b}</p>
            <h3 className="font-display text-[28px] sm:text-[34px] leading-tight mt-1.5" style={{ color: "#3A1A2A" }}>{current.name}</h3>
            <p className="text-[15px] mt-3 leading-relaxed" style={{ color: "rgba(58,26,42,0.65)" }}>{current.what}</p>
            <div className="mt-5 space-y-2.5">
              <div className="rounded-2xl px-5 py-4" style={{ background: "rgba(255,255,255,0.8)", border: "1px solid rgba(58,26,42,0.07)" }}>
                <p className="text-[10px] tracking-[0.16em] uppercase" style={{ color: "rgba(58,26,42,0.45)" }}>usual</p>
                <p className="text-[14px] mt-1" style={{ color: "#3A1A2A" }}>{current.normal}</p>
              </div>
              <div className="rounded-2xl px-5 py-4" style={{ background: "rgba(222,110,150,0.12)", border: "1px solid rgba(222,110,150,0.28)" }} data-testid="wheel-flag">
                <p className="text-[10px] tracking-[0.16em] uppercase font-semibold" style={{ color: "#B04068" }}>take this to a doctor</p>
                <p className="text-[14px] mt-1" style={{ color: "#3A1A2A" }}>{current.flag}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
